import type { VendorConsignmentInvoiceConfig } from "@/generated/graphql";
import { Badge, Button, Can, DataTable } from "@k3-tech/react-kit";
import type { ColumnDef, PaginationState } from "@tanstack/react-table";
import { PencilIcon, PlusIcon } from "lucide-react";
import { useMemo } from "react";
import { describeBillCycle, toDateInputValue } from "../utils/billCycle";

// Scope required by vendorConsignmentInvoiceConfigCreate/Update in transaction.
export const CONSIGNMENT_CONFIG_EDIT_PERMISSION =
	"vendor_consignment_invoice.config";

export interface ConsignmentConfigListProps {
	data: Partial<VendorConsignmentInvoiceConfig>[];
	rowCount?: number;
	loading: boolean;
	onRefresh: () => void;
	paginationState: PaginationState;
	onPaginationChange?: (paginationState: PaginationState) => void;
	onCreate: () => void;
	onEdit: (config: Partial<VendorConsignmentInvoiceConfig>) => void;
}

export const ConsignmentConfigList = ({
	data,
	rowCount,
	loading,
	onRefresh,
	paginationState,
	onPaginationChange,
	onCreate,
	onEdit,
}: ConsignmentConfigListProps) => {
	const columns: ColumnDef<Partial<VendorConsignmentInvoiceConfig>, unknown>[] =
		useMemo(
			() => [
				{
					id: "vendorName",
					header: "Vendor",
					accessorKey: "vendorName",
					cell: ({ row }) => row.original.vendorName || "-",
				},
				{
					id: "cutoffDay",
					header: "Cutoff Day",
					accessorKey: "cutoffDay",
					cell: ({ row }) => (
						<div className="text-right">{row.original.cutoffDay ?? "-"}</div>
					),
				},
				{
					id: "billCycle",
					header: "Bill Cycle",
					cell: ({ row }) => describeBillCycle(row.original),
				},
				{
					id: "billCycleStartDate",
					header: "Cycle Start",
					cell: ({ row }) =>
						toDateInputValue(row.original.billCycleStartDate) || "-",
				},
				{
					id: "graceDays",
					header: "Grace Days",
					accessorKey: "graceDays",
					cell: ({ row }) => (
						<div className="text-right">{row.original.graceDays ?? "-"}</div>
					),
				},
				{
					id: "paymentDueDays",
					header: "Payment Due Days",
					accessorKey: "paymentDueDays",
					cell: ({ row }) => (
						<div className="text-right">
							{row.original.paymentDueDays ?? "-"}
						</div>
					),
				},
				{
					id: "isDefault",
					header: "Default",
					accessorKey: "isDefault",
					cell: ({ row }) =>
						row.original.isDefault ? (
							<Badge
								variant="outline"
								className="border-blue-200 bg-blue-100 text-blue-700"
							>
								Default
							</Badge>
						) : (
							"-"
						),
				},
				{
					id: "isActive",
					header: "Active",
					accessorKey: "isActive",
					cell: ({ row }) =>
						row.original.isActive ? (
							<Badge
								variant="outline"
								className="border-green-200 bg-green-100 text-green-700"
							>
								Active
							</Badge>
						) : (
							<Badge
								variant="outline"
								className="border-zinc-200 bg-zinc-100 text-zinc-500"
							>
								Inactive
							</Badge>
						),
				},
				{
					id: "actions",
					header: "",
					cell: ({ row }) => (
						<Can permissions={CONSIGNMENT_CONFIG_EDIT_PERMISSION}>
							<Button
								type="button"
								variant="ghost"
								size="sm"
								onClick={() => onEdit(row.original)}
							>
								<PencilIcon className="h-4 w-4" />
								Edit
							</Button>
						</Can>
					),
				},
			],
			[onEdit],
		);

	return (
		<div className="space-y-3">
			<div className="flex items-center justify-between gap-3">
				<p className="text-sm text-gray-600">
					Vendors without their own config follow the default. A changed bill
					cycle takes effect from the vendor's next period.
				</p>
				<Can permissions={CONSIGNMENT_CONFIG_EDIT_PERMISSION}>
					<Button type="button" onClick={onCreate}>
						<PlusIcon className="h-4 w-4" />
						Add Vendor Config
					</Button>
				</Can>
			</div>
			<DataTable<Partial<VendorConsignmentInvoiceConfig>, unknown>
				columns={columns}
				data={data}
				loading={loading}
				showStandardActions={true}
				onRefresh={onRefresh}
				rowCount={rowCount}
				pagination
				paginationState={paginationState}
				onPaginationChange={onPaginationChange}
			/>
		</div>
	);
};
